import { useTranslation } from "react-i18next";
import nextId from "react-id-generator";
import { Box, Grid, ListItem, makeStyles, Theme } from "@material-ui/core";
import { Pagination, Skeleton } from "@material-ui/lab";
import { grey } from "@material-ui/core/colors";
import { ItemPropertyLabel } from "./Styled";
import { useState } from "react";

interface Props<T> {
  label: string;
  members: T[];
  renderDetails: (member: T) => React.ReactNode;
  loading?: boolean;
  "data-testid"?: string;
}

export default function MemberDetails<T>(props: Props<T>) {
  const { t } = useTranslation(["accounts", "rules"]);
  const [pagination, setPagination] = useState(initPagination(props.members));
  const classes = useStyles(props);

  if (props.loading) {
    return (
      <>
        {Array.from({ length: 3 }).map((_, index) => (
          <ListItem disableGutters key={`skeleton-${index}`}>
            <Box className={classes.skeletonContainer}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
          </ListItem>
        ))}
      </>
    );
  }

  const onPageChange = (_, page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const getVisibleRows = () => {
    const { page, rowsPerPage } = pagination;

    if (props.members.length > rowsPerPage) {
      const leftBoundary = page * rowsPerPage;
      const rightBoundary = page * rowsPerPage + rowsPerPage;

      return props.members.slice(leftBoundary, rightBoundary);
    }
    return props.members;
  };

  return (
    <>
      <Box mb={2}>
        <ItemPropertyLabel>{props.label}</ItemPropertyLabel>
      </Box>
      <div className={classes.listContainer}>
        <Grid
          container
          alignItems="center"
          xs={12}
          spacing={3}
          data-testid={props["data-testid"]}
        >
          {props.members.length === 0 && (
            <Grid
              item
              xs={4}
              key={nextId()}
              style={{ display: "flex", alignItems: "center" }}
            >
              <span className={classes.noMembers}>{t("noMembers")}</span>
            </Grid>
          )}
          {getVisibleRows().map(member => (
            <Grid
              item
              xs={4}
              key={nextId()}
              style={{ display: "flex", alignItems: "center" }}
            >
              {props.renderDetails(member)}
            </Grid>
          ))}
        </Grid>

        {props.members.length > pagination.rowsPerPage && (
          <Pagination
            color="primary"
            className={classes.pagination}
            page={pagination.page}
            count={pagination.totalPages}
            onChange={onPageChange}
            showFirstButton={true}
            showLastButton={true}
            size="small"
          />
        )}
      </div>
    </>
  );
}

const useStyles = makeStyles<Theme, Props<any>>({
  listContainer: {
    backgroundColor: "#eef2f6",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    marginBottom: "20px",
    maxHeight: "340px",
    overflow: "auto",
    padding: props =>
      calculatePages(props.members.length, 6) > 1
        ? "25px 16px 8px 16px"
        : "16px",
  },
  noMembers: { color: grey[500] },
  skeletonContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  pagination: {
    marginTop: "30px",
    marginLeft: "auto",
    alignSelf: "flex-end",
    "& .MuiPagination-ul > li:first-child": {
      paddingBottom: "0px !important",
    },
  },
});

function calculatePages(totalItems: number, rowsPerPage: number) {
  return Math.round(totalItems / rowsPerPage);
}

function initPagination<T>(members: T[]) {
  const totalItems = (members || []).length;
  const rowsPerPage = 6;
  return {
    page: 1,
    rowsPerPage,
    totalPages: calculatePages(totalItems, rowsPerPage),
  };
}
